import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Wallet, Plus, ArrowDownRight, ArrowUpRight, FileText, Download, Search } from 'lucide-react';
import { format } from 'date-fns';
import { TopUpDialog } from '@/components/dashboard/TopUpDialog';
import { useWallet } from '@/hooks/useWallet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const STATUS_VARIANT: Record<string, any> = {
  approved: 'default',
  awaiting_verification: 'secondary',
  pending: 'secondary',
  rejected: 'destructive',
  cancelled: 'outline',
};

const KIND_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'topup', label: 'Top-Ups' },
  { value: 'package_purchase', label: 'Packages' },
  { value: 'credit_purchase', label: 'Credit Store' },
  { value: 'lead_charge', label: 'Lead Charges' },
  { value: 'refund', label: 'Refunds' },
];

function downloadCsv(rows: any[]) {
  const header = ['Date', 'Direction', 'Kind', 'Amount (ETB)', 'Balance After', 'Note', 'Reference'];
  const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [header.join(',')].concat(
    rows.map((r) => [
      format(new Date(r.created_at), 'yyyy-MM-dd HH:mm'),
      r.direction, r.kind, r.amount_etb, r.balance_after, r.note || '', r.reference_id || '',
    ].map(esc).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `wallet-transactions-${format(new Date(), 'yyyy-MM-dd')}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function WalletPage() {
  const { user } = useAuth();
  const { balance, threshold, lowBalance } = useWallet();
  const [open, setOpen] = useState(false);
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const { data: txs } = useQuery({
    queryKey: ['wallet-txs-all', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('wallet_transactions' as any)
        .select('*').eq('user_id', user!.id)
        .order('created_at', { ascending: false }).limit(500);
      return (data as any[]) || [];
    },
  });

  const { data: requests } = useQuery({
    queryKey: ['wallet-topup-requests', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('wallet_topup_requests' as any)
        .select('*').eq('user_id', user!.id)
        .order('created_at', { ascending: false }).limit(100);
      return (data as any[]) || [];
    },
  });

  const filtered = useMemo(() => {
    let rows = txs || [];
    if (kindFilter !== 'all') rows = rows.filter((t) => t.kind === kindFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((t) =>
        (t.note || '').toLowerCase().includes(q) ||
        (t.kind || '').toLowerCase().includes(q) ||
        (t.reference_id || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [txs, kindFilter, search]);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-primary" /> My Wallet
          </h1>
          <p className="text-sm text-muted-foreground">Top up in ETB and spend on packages, credit packs, and boosts.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Top Up
        </Button>
      </div>

      <Card>
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Available Balance</p>
            <p className="text-4xl font-bold tabular-nums">{balance.toLocaleString()} <span className="text-base font-normal text-muted-foreground">ETB</span></p>
            {lowBalance && <Badge variant="destructive" className="mt-2">Low balance — top up to keep buying packages</Badge>}
          </div>
          <div className="text-right text-sm text-muted-foreground">
            Low-balance alert at <span className="font-medium text-foreground">{threshold.toLocaleString()}</span> ETB
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="transactions">
        <TabsList>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="topups">Top-Up Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3 flex-wrap">
              <CardTitle className="text-base">Recent Activity</CardTitle>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadCsv(filtered)} disabled={!filtered.length}>
                <Download className="h-3.5 w-3.5" /> Export CSV
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Filters */}
              <div className="flex items-center gap-2 flex-wrap">
                <ToggleGroup type="single" value={kindFilter} onValueChange={(v) => v && setKindFilter(v)} className="flex-wrap">
                  {KIND_FILTERS.map((f) => (
                    <ToggleGroupItem key={f.value} value={f.value} size="sm" className="text-xs h-8">
                      {f.label}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Search note or reference…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-7 h-8 text-xs"
                  />
                </div>
              </div>

              {!filtered.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">No matching wallet activity.</p>
              ) : (
                <div className="divide-y">
                  {filtered.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-3 text-sm gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        {t.direction === 'credit'
                          ? <ArrowDownRight className="h-4 w-4 text-emerald-600 shrink-0" />
                          : <ArrowUpRight className="h-4 w-4 text-destructive shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-medium capitalize">{t.kind.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {t.note || '—'} · {format(new Date(t.created_at), 'd MMM yyyy, HH:mm')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p className={`font-semibold tabular-nums ${t.direction === 'credit' ? 'text-emerald-600' : 'text-destructive'}`}>
                            {t.direction === 'credit' ? '+' : '-'}{Number(t.amount_etb).toLocaleString()}
                          </p>
                          <p className="text-[10px] text-muted-foreground">Bal: {Number(t.balance_after).toLocaleString()}</p>
                        </div>
                        <Button asChild size="sm" variant="ghost" className="h-7 px-2 gap-1">
                          <Link to={`/dashboard/wallet/receipt/${t.id}`}>
                            <FileText className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="topups">
          <Card>
            <CardHeader><CardTitle className="text-base">My Top-Up Requests</CardTitle></CardHeader>
            <CardContent>
              {!requests?.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">No top-up requests yet.</p>
              ) : (
                <div className="divide-y">
                  {requests.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-3 text-sm">
                      <div>
                        <p className="font-medium">{Number(r.amount_etb).toLocaleString()} ETB</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {r.method.replace('_', ' ')} · {format(new Date(r.created_at), 'd MMM yyyy')}
                          {r.payment_reference && <> · ref <span className="font-mono">{r.payment_reference}</span></>}
                        </p>
                        {r.rejection_reason && (
                          <p className="text-xs text-destructive mt-1">Reason: {r.rejection_reason}</p>
                        )}
                      </div>
                      <Badge variant={STATUS_VARIANT[r.status] || 'outline'} className="capitalize">
                        {r.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <TopUpDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
